import {
  type CollageBackgroundColor,
  type CollageBackgroundConfig,
  type CollageBackgroundImage,
  type CollageBackgroundTemplate,
  type FeedFiltersResponse,
  type FilterValue,
  type IntroTourCanvasImage,
} from './types';

export const OUTFIT_FEATURE_BANNER_PREFIX = 'seenOutfitCoreFeatures';
export const OUTFIT_PRESSED_SCALE = 1.05;
export const BACK_EVENT_NAME = 'nativeBackClick';
export const SAVE_COLLAGE_EVENT_NAME = 'nativeSaveCollage';
export const UPDATE_COLLAGE_EVENT_NAME = 'nativeUpdateCollage';
export const DELETE_EVENT_NAME = 'nativeDeleteClick';
export const CLOSE_EVENT_NAME = 'nativeCloseClick';
export const TOUR_END_EVENT_NAME = 'nativeTutorialFinished';
export const RESTYLE_FAILED_EVENT_NAME = 'nativeRestyleFailed';

export const COLLAGE_BACKGROUNDS: CollageBackgroundConfig[] = [
  { type: 'color', value: '#F3F3F3' },
  { type: 'color', value: '#E3E3E3' },
  { type: 'color', value: '#D7D7D7' },
  { type: 'color', value: '#FFF6E1' },
  { type: 'color', value: '#FFE9AF' },
  { type: 'color', value: '#FFDE86' },
  { type: 'color', value: '#EDEDF7' },
  { type: 'color', value: '#DBDAEE' },
  { type: 'color', value: '#B8B5DD' },
  { type: 'color', value: '#F6EBF0' },
  { type: 'color', value: '#EDD6E0' },
  { type: 'color', value: '#DCAEC1' },
  { type: 'color', value: '#F9C9C2' },
  { type: 'color', value: '#F6A599' },
  { type: 'color', value: '#F17966' },
  { type: 'color', value: '#E9F3EB' },
  { type: 'color', value: '#D3E6D7' },
  { type: 'color', value: '#A8CDAF' },
  {
    type: 'linear',
    colors: [
      { offset: 0, color: '#FBF6F1' },
      { offset: 0.9128, color: '#E2E8F7' },
    ],
    value: 'linear-gradient(270deg, #FBF6F1 0%, #E2E8F7 91.28%)',
  },
  {
    type: 'linear',
    colors: [
      { offset: -0.2339, color: '#EDD7E3' },
      { offset: 0.8899, color: '#FBF6F1' },
    ],
    value: 'linear-gradient(270deg, #EDD7E3 -23.39%, #FBF6F1 88.99%)',
  },
];

export const DEFAULT_BACKGROUND_COLOR: CollageBackgroundColor = {
  type: 'color',
  value: '#F3F3F3',
};

export const FALLBACK_BACKGROUND_COLOR: CollageBackgroundColor = {
  type: 'color',
  value: '#FFFFFF',
};
export const DEFAULT_IMAGE_BORDER_COLOR = '#504BC4';
export const DEFAULT_IMAGE_SCALE = 0.66;
export const IMAGE_WIDTH_LARGE = 1280;
export const IMAGE_WIDTH_MEDIUM = 960;
export const COLLAGE_ITEM_IMAGE_MEDIUM = 540;

export const PRODUCTS_SEARCH_ITEMS_PER_PAGE = 24;
export const COLLAGE_RECOMMENDED_ITEMS_LIMIT = 24;
export const MINIMAL_PINCH_SCALE = 0.3;
export const MAXIMAL_PINCH_SCALE = 4;

export const CANVAS_LOAD_ANIMATION_DURATION = 1200;

export enum OutfitCategoryType {
  Recommended = 'recs',
  Likes = 'likes',
  Listings = 'listings',
}

const INTRO_TOUR_IMAGE_PATH = `${process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST}/web/assets/outfits`;
export const INTRO_TOUR_CANVAS_IMAGE: IntroTourCanvasImage = {
  productId: 9999, // Special ID for intro tour
  path: `${INTRO_TOUR_IMAGE_PATH}/intro-tour-image-2.png`,
  name: 'intro-tour-image-2.png',
};

const WEB_ASSETS_TEMPLATE_PATH = `${process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST}/web/assets/outfits/templates`;
export const TEMPLATES: CollageBackgroundTemplate[] = [
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-1.jpg`,
  },
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-2.jpg`,
  },
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-3.jpg`,
  },
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-4.jpg`,
  },
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-5.jpg`,
  },
  {
    type: 'template',
    value: `${WEB_ASSETS_TEMPLATE_PATH}/template-thumbnail-6.jpg`,
  },
];

const WEB_ASSETS_COMPETITIONS_PATH = `${process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST}/web/assets/outfits/competitions`;
export const BACKGROUND_IMAGES: CollageBackgroundImage[] = [
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_be_my_bridesmaid.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_sultriness.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_girls_weekend.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_chestnut.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_teddy.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_off_the_market.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_ruby_woo.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_caviar.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_lipstick_snob.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_kinda_sexy.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_folio.jpg`,
  },
  {
    type: 'image',
    value: `${WEB_ASSETS_COMPETITIONS_PATH}/background_diva.jpg`,
  },
];

export const ROTATE_ICON = `
<svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.7778 12C19.7778 16.2955 16.266 19.7778 11.934 19.7778C9.79676 19.7778 7.85919 18.9302 6.44446 17.5556"
        stroke="#504BC4"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M22 12H17.5555L19.7778 7.55554L22 12Z" fill="#504BC4" />
      <path
        d="M4.22221 12C4.22221 7.70445 7.734 4.22222 12.066 4.22222C14.2032 4.22222 16.1408 5.06979 17.5555 6.44445"
        stroke="#504BC4"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M2.00001 12L6.44446 12L4.22224 16.4445L2.00001 12Z"
        fill="#504BC4"
      />
    </svg>
    `;

export const LOCALE_EN_US = 'en-us' as const;
export const LOCALE_EN_GB = 'en-gb' as const;

export function L(us: string, gb?: string) {
  return {
    [LOCALE_EN_GB]: gb ?? us,
    [LOCALE_EN_US]: us,
  } as const;
}

export const FALLBACK_FEED_FILTERS: FeedFiltersResponse = {
  categories: [
    {
      id: 'gender',
      copy: L('Gender'),
      values: [
        { id: 'all', copy: L('All') },
        { id: 'men', copy: L('Men') },
        { id: 'women', copy: L('Women') },
        { id: 'unisex', copy: L('Unisex') },
      ],
    },
  ],
  filters: [
    {
      id: 'season',
      copy: L('Season'),
      values: [
        { id: 'spring', copy: L('Spring') },
        { id: 'summer', copy: L('Summer') },
        { id: 'autumn', copy: L('Fall', 'Autumn') },
        { id: 'winter', copy: L('Winter') },
      ],
    },
    {
      id: 'style',
      copy: L('Style'),
      values: [
        { id: 'casual', copy: L('Casual') },
        { id: 'glam', copy: L('Glam') },
        { id: 'minimalist', copy: L('Minimalist') },
        { id: 'vintage', copy: L('Vintage') },
        { id: 'streetwear', copy: L('Streetwear') },
        { id: 'alternative', copy: L('Alternative') },
        { id: 'bohemian', copy: L('Bohemian') },
        { id: 'preppy', copy: L('Preppy') },
        { id: 'formal', copy: L('Formal') },
        { id: 'western', copy: L('Western') },
        { id: 'sporty', copy: L('Sporty') },
        { id: 'athleisure', copy: L('Athleisure') },
      ],
    },
  ],
};

export const MODERATION_DEFAULT_GENDER_FILTERS: FilterValue[] = [
  { id: 'all', copy: { [LOCALE_EN_US]: 'All' } },
  { id: 'men', copy: { [LOCALE_EN_US]: 'Men' } },
  { id: 'women', copy: { [LOCALE_EN_US]: 'Women' } },
  { id: 'unisex', copy: { [LOCALE_EN_US]: 'Unisex' } },
];

export const MODERATION_DEFAULT_SEASON_FILTERS: FilterValue[] = [
  { id: 'spring', copy: { [LOCALE_EN_US]: 'Spring' } },
  { id: 'summer', copy: { [LOCALE_EN_US]: 'Summer' } },
  { id: 'autumn', copy: { [LOCALE_EN_US]: 'Autumn' } },
  { id: 'winter', copy: { [LOCALE_EN_US]: 'Winter' } },
];

export const ALL_STYLES_OPTION = { value: '', label: 'All styles' };

export const SORTING_OPTIONS = [
  { value: 'recency', label: 'Recency' },
  { value: 'relevancy', label: 'Relevancy' },
] as const;

export const MAX_RETRIES = 3;
export const MAX_CUTOUT_ATTEMPTS = 2;

export const OUTFIT_TEMPLATE_PREVIEW_MAX_WIDTH = 320;
export const OUTFIT_TEMPLATE_PREVIEW_MAX_HEIGHT = 420;
export const OUTFIT_TEMPLATE_PREVIEW_THUMB_WIDTH = 64;
